/* ============================================================================
   VehiclesVesselsPage — LOCKED VISUAL (Tropical Money, operator dashboard)
   ----------------------------------------------------------------------------
   Pixel-for-pixel, feature-for-feature port of the approved HTML prototype
   (AQX_07-25-2026_Vehicles_Vessels_Prototype_v1). This file and
   VehiclesVesselsPage.css are the visual truth and must not change during
   wiring. All data and mutations come from useVehiclesVessels() — the seam.
   The modal is portaled to document.body so the app shell's transforms and
   stacking context cannot clip or contain it.
   ========================================================================= */
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useVehiclesVessels, Asset, AssetType, NewAsset } from "./useVehiclesVessels";
import "./VehiclesVesselsPage.css";

type Filter = "all" | AssetType;
type ModalKind = null | "create" | "edit";

const TYPES: AssetType[] = ["vehicle", "vessel", "trolley"];
const UNIT: Record<AssetType, string> = { trolley: "gear sets", vehicle: "seats", vessel: "divers" };

const IconVehicle = () => (<svg viewBox="0 0 24 24"><path d="M3 13l2-5a2 2 0 0 1 1.9-1.3h10.2A2 2 0 0 1 19 8l2 5v5h-3M6 18H3v-5" /><circle cx="7.5" cy="18" r="1.6" /><circle cx="16.5" cy="18" r="1.6" /></svg>);
const IconVessel = () => (<svg viewBox="0 0 24 24"><path d="M3 17c1.5 1.2 3 1.2 4.5 0s3-1.2 4.5 0 3 1.2 4.5 0 3-1.2 4.5 0" /><path d="M5 15l2-6h9l2 6" /><path d="M11 9V4l5 2-5 1" /></svg>);
const IconTrolley = () => (<svg viewBox="0 0 24 24"><path d="M4 5h3l2.2 9.2A1.5 1.5 0 0 0 10.7 15H18" /><path d="M9 8h11l-1.5 5H10" /><circle cx="11" cy="19" r="1.4" /><circle cx="17" cy="19" r="1.4" /></svg>);
const IconEdit = () => (<svg viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>);
const IconX = () => (<svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>);
const IconPlus = () => (<svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>);
const IconSearch = () => (<svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></svg>);

const TypeIcon = ({ t }: { t: AssetType }) => (t === "vehicle" ? <IconVehicle /> : t === "vessel" ? <IconVessel /> : <IconTrolley />);

export default function VehiclesVesselsPage() {
  const api = useVehiclesVessels();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [modal, setModal] = useState<ModalKind>(null);
  const [editId, setEditId] = useState<number | null>(null);

  const assets = api.assets;
  const editAsset = editId != null ? assets.find(a => a.id === editId) || null : null;

  const stats = useMemo(() => ({
    vehicle: assets.filter(a => a.active && a.type === "vehicle").length,
    vessel: assets.filter(a => a.active && a.type === "vessel").length,
    trolley: assets.filter(a => a.active && a.type === "trolley").length,
  }), [assets]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assets.filter(a =>
      (filter === "all" || a.type === filter) &&
      (!q || a.name.toLowerCase().includes(q) || a.desc.toLowerCase().includes(q) || a.notes.toLowerCase().includes(q)));
  }, [assets, search, filter]);

  const active = shown.filter(a => a.active);
  const inactive = shown.filter(a => !a.active);

  const openCreate = () => { setEditId(null); setModal("create"); };
  const openEdit = (id: number) => { setEditId(id); setModal("edit"); };
  const close = () => { setModal(null); setEditId(null); };

  return (
    <div className="aqx-vv">
      <div className="vv-content">
        <h1 className="vv-h1">Vehicles &amp; Vessels</h1>
        <p className="vv-lede">Define the vehicles and vessels your center uses — your trolleys, vans, and boats. Name them here so your crew can assign the right one when they build the day, and the schedule and Ops Board reflect what is really running.</p>

        <div className="vv-toolbar">
          <div className="vv-stats">
            <div className="vv-stat"><b>{stats.vehicle}</b><span>Vehicles</span></div>
            <div className="vv-stat"><b>{stats.vessel}</b><span>Vessels</span></div>
            <div className="vv-stat"><b>{stats.trolley}</b><span>Trolleys</span></div>
          </div>
          <div className="vv-spacer" />
          <div className="vv-search"><IconSearch /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search your vehicles and vessels" /></div>
          <div className="vv-seg">
            {(["all", "vehicle", "vessel", "trolley"] as Filter[]).map(f => (
              <button key={f} className={filter === f ? "on" : ""} onClick={() => setFilter(f)}>
                {f === "all" ? "All" : f === "vehicle" ? "Vehicles" : f === "vessel" ? "Vessels" : "Trolleys"}
              </button>
            ))}
          </div>
          <button className="vv-btn primary" onClick={openCreate}><IconPlus />Add vehicle or vessel</button>
        </div>

        <div className="vv-secrow"><span className="vv-seclabel">In service</span><span className="n">{active.length}</span></div>
        {active.length ? <AssetTable rows={active} openEdit={openEdit} api={api} />
          : <div className="vv-empty">Nothing in service here yet. Use <b>Add vehicle or vessel</b> to name your first trolley, van, or boat.</div>}

        <div className="vv-secrow"><span className="vv-seclabel">Out of service</span><span className="n">{inactive.length}</span></div>
        {inactive.length ? <AssetTable rows={inactive} openEdit={openEdit} api={api} />
          : <div className="vv-empty">Nothing out of service. Assets you retire rest here — kept, never deleted.</div>}
      </div>

      {modal && createPortal(
        <div className="aqx-vv">
          <div className="vv-scrim" onClick={close} />
          <AssetModal asset={modal === "edit" ? editAsset : null} api={api} onClose={close} />
        </div>,
        document.body
      )}
    </div>
  );
}

function capText(a: Asset) {
  return a.cap ? <>{a.cap} <small>{UNIT[a.type]}</small></> : <small>—</small>;
}

function AssetTable({ rows, openEdit, api }: { rows: Asset[]; openEdit: (id: number) => void; api: ReturnType<typeof useVehiclesVessels>; }) {
  return (
    <div className="vv-tablewrap">
      <table>
        <thead><tr><th>Name</th><th>Type</th><th>Capacity</th><th>Status</th><th style={{ textAlign: "right" }}>Actions</th></tr></thead>
        <tbody>
          {rows.map(a => (
            <tr key={a.id}>
              <td>
                <button className="vv-name" onClick={() => openEdit(a.id)}>{a.name}</button>
                {(a.desc || a.notes) && <div className="vv-sub">{[a.desc, a.notes].filter(Boolean).join(" · ")}</div>}
              </td>
              <td><span className={`vv-chip ${a.type}`}><TypeIcon t={a.type} />{a.type}</span></td>
              <td className="cap">{capText(a)}</td>
              <td><span className={`vv-pill ${a.active ? "active" : "quiet"}`}><span className="d" />{a.active ? "Active" : "Inactive"}</span></td>
              <td className="act">
                <button className="vv-btn ghost" onClick={() => openEdit(a.id)}><IconEdit /> Edit</button>
                {a.active
                  ? <button className="vv-btn ghost" onClick={() => api.setActive(a.id, false)}>Deactivate</button>
                  : <button className="vv-btn reef" onClick={() => api.setActive(a.id, true)}>Reactivate</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssetModal({ asset, api, onClose }: { asset: Asset | null; api: ReturnType<typeof useVehiclesVessels>; onClose: () => void; }) {
  const [name, setName] = useState(asset?.name ?? "");
  const [type, setType] = useState<AssetType>(asset?.type ?? "vehicle");
  const [cap, setCap] = useState<string>(asset ? String(asset.cap || "") : "");
  const [desc, setDesc] = useState(asset?.desc ?? "");
  const [notes, setNotes] = useState(asset?.notes ?? "");
  const [active, setActive] = useState(asset?.active ?? true);
  const [err, setErr] = useState(false);

  const save = () => {
    const nm = name.trim();
    if (!nm) { setErr(true); return; }
    const n = parseInt(cap, 10);
    const patch: NewAsset = { name: nm, type, cap: isNaN(n) ? 0 : n, desc: desc.trim(), notes: notes.trim(), active };
    if (asset) api.updateAsset(asset.id, patch);
    else api.createAsset(patch);
    onClose();
  };

  return (
    <div className="vv-modal">
      <div className="vv-mh">
        <h3>{asset ? `Edit ${asset.name}` : "Add a vehicle or vessel"}</h3>
        <button className="x" onClick={onClose}><IconX /></button>
      </div>
      <div className="vv-mb">
        <div className="vv-flabel first">Name</div>
        <input className="vv-inp" value={name} onChange={e => { setName(e.target.value); setErr(false); }} placeholder="e.g. Blue Van, RIB One, Gear Trolley A" style={err ? { borderColor: "var(--warm)" } : undefined} />

        <div className="vv-flabel">Type</div>
        <div className="vv-typebtns">
          {TYPES.map(t => (
            <button key={t} className={`vv-typebtn ${type === t ? "on" : ""}`} onClick={() => setType(t)}><TypeIcon t={t} /><span>{t}</span></button>
          ))}
        </div>

        <div className="vv-row2">
          <div>
            <div className="vv-flabel">Capacity</div>
            <input className="vv-inp" type="number" min={0} value={cap} onChange={e => setCap(e.target.value)} placeholder="0" />
            <div className="vv-hint">Number of {UNIT[type]} it carries.</div>
          </div>
          <div>
            <div className="vv-flabel">Descriptor <span className="vv-sublabel">(optional)</span></div>
            <input className="vv-inp" value={desc} onChange={e => setDesc(e.target.value)} placeholder="e.g. Day boat, Hiace, RIB" />
          </div>
        </div>

        <div className="vv-flabel">Notes <span className="vv-sublabel">(optional)</span></div>
        <textarea className="vv-inp" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Anything your crew should know" />

        <div className="vv-flabel">Status</div>
        <span className={`vv-sw ${active ? "on" : ""}`} onClick={() => setActive(a => !a)}>
          <span className="track" />{active ? "Active — in service" : "Inactive — resting"}
        </span>
      </div>
      <div className="vv-mf">
        <button className="vv-btn primary" onClick={save}>Save</button>
        <button className="vv-btn" onClick={onClose}>Cancel</button>
        <div style={{ flex: 1 }} />
        {asset && <button className="vv-btn ghost" onClick={() => { api.setActive(asset.id, !asset.active); onClose(); }}>{asset.active ? "Deactivate" : "Reactivate"}</button>}
      </div>
    </div>
  );
}
